import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAxios } from "../../../api/axios";
import { useTranslation } from "react-i18next";
import useProperty from "../../context/useProperty";
import { searchProperties } from "../../../stories/investor/amplitude";
import { useEffect } from "react";

export interface PubPropertyPayload {
  listType: string;
  page: number;
  limit: number;
  searchTxt?: string;
  state?: string;
  offMarket?: boolean;
  occupancy?: string;
  salesInstruction?: string;
  status?: string;
  possession?: string;
  mapBounds: {
    topLeftLng: number;
    topLeftLat: number;
    bottomRightLng: number;
    bottomRightLat: number;
  };
  orderBy?: string;
  order?: string;
}

export const useInvestorPropertyList = ({ type }: { type: string }) => {
  const { coords } = useProperty();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const axios = useAxios(type);

  const urlKey = `${t("investor-property-list-api")}`;
  const searchTextValue = searchParams.get("searchText");
  const sortValue = searchParams.get("order");
  const orderValue = searchParams.get("sort");

  const createPayload = (page: number, limit: number): PubPropertyPayload => {
    let payload: PubPropertyPayload = {
      listType: "list",
      page,
      limit,
      mapBounds: {
        topLeftLng: coords.topLeftLng,
        topLeftLat: coords.topLeftLat,
        bottomRightLat: coords.bottomRightLat,
        bottomRightLng: coords.bottomRightLng
      }
    };

    if (searchTextValue) {
      payload = { ...payload, searchTxt: searchTextValue };
      if (type === "investor") {
        // Get full_count from the first page if available
        const firstPage = arrProperty?.pages?.[0];
        const totalCount = firstPage?.data?.[0]?.full_count || 0;
        searchProperties(searchTextValue, totalCount);
      }
    }
    if (sortValue) {
      payload = { ...payload, order: sortValue.toUpperCase() };
    }
    if (orderValue) {
      payload = { ...payload, orderBy: orderValue };
    }
    return payload;
  };
  const fetchProperties = async ({ pageParam = 1 }) => {
    const payload = createPayload(pageParam, 10); // 10 items per page
    const res = await axios.post(urlKey, payload);
    return {
      data: res.data.data, // Array of properties
      hasNextPage: true, // Boolean indicating if there are more pages
      currentPage: pageParam // Current page number
    };
  };

  const {
    data: arrProperty,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    isPlaceholderData
  } = useInfiniteQuery({
    queryKey: [
      `investor-property-list`,
      coords,
      searchTextValue,
      sortValue
    ],
    queryFn: fetchProperties,
    initialPageParam: 1,
    placeholderData: (oldData) => oldData,
    getNextPageParam: (lastPage: any, allPages: any) => {
      const totalItems = parseInt(allPages[0].data[0]?.full_count, 10) || 0; // Default to 0 if parsing fails
      const totalPages = Math.ceil(totalItems / 10); // Assuming 10 items per page
      const _nextPage =
        lastPage.currentPage < totalPages ? allPages.length + 1 : undefined;
      return _nextPage;
    },
    enabled: !!searchParams && type === "investor",
    refetchOnWindowFocus: false, // Disable refetching when the window regains focus
    refetchOnReconnect: false // Disable refetching when internet connection is restored
  });

  // Add useEffect to track search results when they change
  useEffect(() => {
    if (type === "investor" && searchTextValue && arrProperty?.pages?.[0]) {
      const totalCount = arrProperty.pages[0].data[0]?.full_count || 0;
      searchProperties(searchTextValue, totalCount);
    }
  }, [arrProperty?.pages, searchTextValue, type]);

  return {
    arrProperty,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    isPlaceholderData
  };
};
